import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import { BrandRecomSchema } from "model/contents/brand-recom.schema";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import Image from "components/Common/Image";
import { getImageUrl, ImageSourceType } from "utils/imageUtils";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";
import { useAddBrandRecomToHighlightMutation } from "services/contents/api";

const BrandRecomNomaroiTable = ({ data, isLoading, error, onTableChange }) => {
  const { modal } = useModal();
  const { addToast, removeToast } = useToasts();
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [addBrandRecomToHighlight, { isLoading: isAdding }] = useAddBrandRecomToHighlightMutation();

  // HOOK CHANGE
  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "รายการ Brand Affiliate",
        accessor: "name",
        sortable: true,
        render: (item) => (
          <div className="brand-info d-flex align-items-center gap-3">
            <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
            <div>
              <div className="text-lg fw-semibold">{item.name}</div>
            </div>
          </div>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <div
            className="text-gray-500 cursor-pointer"
            onClick={(event) => {
              event.stopPropagation();
              handleAddNomaroi(item);
            }}
          >
            <i className="bx bx-plus"></i>
          </div>
        ),
      },
    ],
    []
  );

  const handleAddNomaroi = async (item) => {
    const result = await modal.alert.primary({
      title: "ยืนยันที่จะเพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำ",
      message: `ตรวจสอบรายละเอียดแบรนด์ที่คุณต้องการเพิ่มเข้าไปยังแบรนด์ที่แนะนำว่าถูกต้องหรือไม่?`,
      confirmText: "ยืนยันที่จะเพิ่ม",
      cancelText: "ยกเลิก",
      children: (
        <div className="brand-info d-flex align-items-center gap-3">
          <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
          <div>
            <div className="text-lg fw-semibold">{item.name}</div>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await addBrandRecomToHighlight(item);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title="เพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำแล้ว" description={`แบรนด์ "${item.name}" ถูกเพิ่มไปยังแบรนด์ที่แนะนำเรียบร้อยแล้ว`} onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to add brand to highlight:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleAddSelected = async (items) => {
    const result = await modal.alert.primary({
      title: "ยืนยันที่จะเพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำ",
      message: `คุณได้ทำการกดเพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำในระบบทั้งหมด "${items.length} รายการ"`,
      confirmText: "ยืนยันที่จะเพิ่ม",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-3 divide-y">
          {items.map((item, index) => (
            <div key={item.id} className={`d-flex flex-column gap-2 ${index > 0 && "pt-3"}`}>
              <div className="brand-info d-flex align-items-center gap-3">
                <Image src={getImageUrl(item.imgThumbnail, { type: ImageSourceType.NOMAROI })} alt={item.name} />
                <div>
                  <div className="text-lg fw-semibold">{item.name}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ),
    });
    if (result) {
      try {
        await addBrandRecomToHighlight(items);
        const toastId = addToast(<ToastNotification variant="success" icon={check} title={`เพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำแล้ว ${selectedBrands.length} รายการ`} description="แบรนด์ที่เลือกถูกเพิ่มไปยังแบรนด์ที่แนะนำเรียบร้อยแล้ว" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
        // Clear selection
        setSelectedBrands([]);
      } catch (error) {
        console.error("Failed to add brands to highlight:", error);
        const toastId = addToast(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถเพิ่มแบรนด์ไปยังแบรนด์ที่แนะนำได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => removeToast(toastId)} />, {
          autoDismiss: true,
        });
      }
    }
  };

  const handleSelectionChange = (selected) => {
    setSelectedBrands(selected);
  };

  return (
    <div className="mt-4">
      <CustomTable
        keyField="id"
        data={data?.data || []}
        columns={columns}
        selectable={true}
        onSelectionChange={handleSelectionChange}
        selectionActions={
          <a
            className="success"
            onClick={() => {
              if (selectedBrands.length > 0) {
                handleAddSelected(selectedBrands);
              }
            }}
          >
            <i className="bx bx-plus" />
            <span className="d-none d-md-block">เพิ่มรายการที่เลือกไว้</span>
          </a>
        }
        itemsPerPage={data?.pagination?.limit || 10}
        totalItems={data?.pagination?.total || 0}
        onSort={handleSort}
        onFilter={handleFilter}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"}
        errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"}
        emptyMessage="ไม่พบข้อมูลสินค้า Affiliate"
      />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    // Skip validation if loading or error
    if (props.isLoading || props.error) {
      return <WrappedComponent {...props} />;
    }

    try {
      schema.validateSync(props.data);
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(BrandRecomNomaroiTable, BrandRecomSchema);

export default ValidatedTable;
